import React, { useState, useMemo, useEffect } from 'react';
import {
  PortfolioAdjustment,
  FundCombination,
  FundProductCard
} from './components/RecommendationsTab';
import ActionBar from './components/ActionBar.tsx';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  mockFundData,
  type FundData
} from './components/RecommendationsTab/mockData';
import { calculateAdjustment } from './components/RecommendationsTab/utils';
import { TotalData, RecommendationsTabKeyNames } from './index.type';
import { extractNumber } from '@/lib/utils';

const RecommendationsTab: React.FC<{
  totalData: Pick<TotalData, RecommendationsTabKeyNames>;
}> = ({ totalData }) => {
  // 基金数据状态
  const [fundData, setFundData] = useState<FundData>(mockFundData);

  useEffect(() => {
    const copyFundData = JSON.parse(JSON.stringify(fundData));
    const { cash_afterRatio, stable_afterRatio, aggressive_afterRatio } =
      calculateAdjustment(totalData);
    Object.keys(copyFundData).forEach(key => {
      if (key === 'cash') {
        copyFundData[key].suggestRatio = cash_afterRatio.toString() + '%';
      } else if (key === 'conservative') {
        copyFundData[key].suggestRatio = stable_afterRatio.toString() + '%';
      } else if (key === 'aggressive') {
        copyFundData[key].suggestRatio = aggressive_afterRatio.toString() + '%';
      }
      copyFundData[key].fundProducts.forEach(item => {
        item.name = totalData[item.key as keyof TotalData];
        item.metrics.forEach(metric => {
          metric.value = extractNumber(
            totalData[metric.key as keyof TotalData] || '0'
          );
        });
      });
    });
    setFundData(copyFundData);
  }, [totalData]);

  // 选中的指标
  const [selectedMetrics, setSelectedMetrics] = useState({
    industryDistribution: true,
    tradingEvaluation: false,
    fundProducts: true,
    conservative: true,
    aggressive: true
  });

  // 全选状态
  const selectAll = useMemo(() => {
    const values = Object.values(selectedMetrics);
    return values.length > 0 && values.every(v => v);
  }, [selectedMetrics]);

  // 半选状态
  const isIndeterminate = useMemo(() => {
    const values = Object.values(selectedMetrics);
    const checkedCount = values.filter(v => v).length;
    return checkedCount > 0 && checkedCount < values.length;
  }, [selectedMetrics]);

  // 处理全选
  const handleSelectAll = (value: boolean) => {
    const newMetrics = { ...selectedMetrics };
    Object.keys(newMetrics).forEach(key => {
      newMetrics[key as keyof typeof selectedMetrics] = value;
    });
    setSelectedMetrics(newMetrics);
  };

  const handleSelectChange = (key: string, checked: boolean) => {
    setSelectedMetrics(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  return (
    <>
      <ScrollArea className="scroll-area-block h-full w-full">
        <div className="rounded-lg bg-white p-4 pb-16">
          {/* 持仓结构调整建议 */}
          <PortfolioAdjustment
            selectedMetrics={selectedMetrics}
            selectChange={handleSelectChange}
            totalData={totalData}
          />
          {/* 推荐基金产品组合 */}
          <FundCombination selectedMetrics={selectedMetrics} />

          {/* 基金产品卡片 - 动态渲染 */}
          {Object.entries(fundData).map(([key, data]) => (
            <FundProductCard
              key={key}
              suggestRatio={data.suggestRatio}
              selectedMetrics={selectedMetrics}
              title={data.title}
              fundProducts={data.fundProducts}
              onSelectChange={handleSelectChange}
              checkboxKey={key}
            />
          ))}
        </div>
      </ScrollArea>
      {/* 操作栏 */}
      <ActionBar
        selectAll={selectAll}
        isIndeterminate={isIndeterminate}
        onUpdateSelectAll={handleSelectAll}
      />
    </>
  );
};

export default RecommendationsTab;
